import type { Identity } from '../types/auth';
import type { KanbanBoard, KanbanBoardPermissions } from '../types/kanban';
import { teamStaffOnlyKanbanRead, teamStaffOnlyKanbanWrite } from './tesseractTeamAccess';

/** Teams returned when the API sends the super-user wildcard. */
export const KANBAN_DEFAULT_TEAMS = ['devteam', 'buildteam', 'prteam', 'eventteam'] as const;

/** Normalize `GET /kanban/teams` payload to concrete team ids. */
export function normalizeKanbanTeamsResponse(teams: unknown): string[] {
  if (!Array.isArray(teams)) return [];
  const strings = teams.filter((t): t is string => typeof t === 'string');
  if (strings.includes('*')) return [...KANBAN_DEFAULT_TEAMS];
  return [...strings];
}

const KANBAN_TEAM_ACTION = /^argus\.kanban\.(read|write|manage)\.(.+)$/;

/**
 * Team ids from JWT `permissions` for any kanban scope (read / write / manage).
 * Used when `/kanban/teams` is empty until a board exists for that team.
 */
export function kanbanTeamsFromPermissions(permissions: unknown): string[] {
  if (!Array.isArray(permissions)) return [];
  const set = new Set<string>();
  for (const p of permissions) {
    if (typeof p !== 'string') continue;
    const m = p.match(KANBAN_TEAM_ACTION);
    if (m) set.add(m[2]);
  }
  return Array.from(set).sort();
}

/** Merge API teams with JWT-derived teams, de-duplicated, stable sort. */
export function mergeKanbanTeamLists(apiTeams: string[], jwtTeams: string[]): string[] {
  return Array.from(new Set([...apiTeams, ...jwtTeams])).sort();
}

/** Staff team ids from `_whoami` / identity `custom.staff_teams` (may include teams before JWT permissions sync). */
export function kanbanTeamsFromStaffTeams(staffTeams: unknown): string[] {
  if (!Array.isArray(staffTeams)) return [];
  return staffTeams.filter((t): t is string => typeof t === 'string' && t.trim() !== '');
}

/** Direct board grants are only known by the API; authenticated users can open the workspace shell. */
export function canViewKanbanWorkspaces(identity: Identity | null, isDev = false): boolean {
  return isDev || identity != null;
}

/** Server capabilities account for board grants and staff-role aliases; old servers use existing JWT scopes. */
export function getKanbanBoardPermissions(
  identity: Identity | null,
  isDev: boolean,
  board: KanbanBoard | null | undefined,
): KanbanBoardPermissions {
  const none = { read: false, write: false, manage: false, manage_access: false };
  if (isDev) return { read: true, write: true, manage: true, manage_access: true };
  if (!identity) return none;
  if (board?.permissions) return board.permissions;
  const permissions = identity.raw?.custom?.permissions ?? [];
  const groups = identity.raw?.custom?.groups ?? [];
  const superUser = groups.includes('SPY_Super_admin') || permissions.includes('argus.kanban.super');
  if (superUser) return { read: true, write: true, manage: true, manage_access: true };
  if (!board) return none;
  const has = (action: string) => permissions.includes(`argus.kanban.${action}.${board.team}`) ||
    groups.includes(`argus_kanban_${action}_${board.team}`);
  const normalizeUuid = (uuid: string) => uuid.toLowerCase().replace(/-/g, '');
  const creator = !!board.created_by && [identity.user_uuid, identity.raw?.custom?.minecraft_uuid]
    .some((uuid) => !!uuid && normalizeUuid(uuid) === normalizeUuid(board.created_by));
  const manage = has('manage');
  const write = manage || has('write') || teamStaffOnlyKanbanWrite(identity, isDev, board.team);
  return {
    read: creator || write || has('read') || teamStaffOnlyKanbanRead(identity, isDev, board.team),
    write,
    manage,
    manage_access: creator || manage,
  };
}
